import type { Line } from '@/types/calls-types';
import {
  type DialingState,
  type SessionContact,
  getSessionContactKey,
} from '@/views/power-dialer/types';
import type { VoiceCallStateStatus } from '@packages/client-sdk-react/src/voice/voice-call-container';

export type Phase = 'idle' | 'countdown' | 'dialing' | 'paused' | 'ended';

export type InFlight = {
  contactKey: string;
  attemptId: string;
  everConnected: boolean;
  skipRequested: boolean;
};

export type PowerDialerState = {
  phase: Phase;
  sessionId: string | null;
  queueId: string | null;
  currentIndex: number;
  inFlight: InFlight | null;
  line: Line | null;
  /** Snapshot of contacts pinned at START_SESSION so the reducer can compute
   *  end-of-queue / advance without reaching outside. Refreshed by
   *  QUEUE_MUTATED when the live list changes mid-session. */
  contacts: SessionContact[];
  dialingState: Record<string, DialingState>;
};

export const INITIAL_STATE: PowerDialerState = {
  phase: 'idle',
  sessionId: null,
  queueId: null,
  currentIndex: 0,
  inFlight: null,
  line: null,
  contacts: [],
  dialingState: {},
};

export type PowerDialerAction =
  | { type: 'START_REQUESTED' }
  | { type: 'COUNTDOWN_CANCELLED' }
  | {
      type: 'START_SESSION';
      sessionId: string;
      queueId: string | null;
      line: Line;
      contacts: SessionContact[];
    }
  | { type: 'DIAL_CONTACT'; contactKey: string; attemptId: string }
  | { type: 'DIAL_FAILED'; contactKey: string }
  | { type: 'CALL_START_UNRECOVERABLE'; contactKey: string; attemptId: string }
  | {
      type: 'CALL_STATUS_CHANGED';
      prev: VoiceCallStateStatus;
      next: VoiceCallStateStatus;
    }
  /** User asked to skip a live in-flight call. The cursor stays pinned until
   *  the SDK reports the call is actually idle. */
  | { type: 'SKIP_REQUESTED' }
  /** User-driven advance when no live call exists. Finalizes the current row
   *  (if not already) and moves the cursor forward. */
  | { type: 'ADVANCE_REQUESTED' }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'END_REQUESTED' }
  /** Another client claimed this session. Drop the local loop without going
   *  through `ended` — the session is still alive on the server, so the
   *  teardown effect's `POST .../end` must not fire. */
  | { type: 'SESSION_CLAIMED_ELSEWHERE'; sessionId: string }
  | { type: 'ENDED_CLEANUP_COMPLETE' }
  | { type: 'QUEUE_MUTATED'; queueId: string | null; contacts: SessionContact[] }
  | { type: 'RESTART_FROM_INDEX'; index: number };

/** Build the stable idempotency key the effect passes into DIAL_CONTACT. */
export const makeAttemptId = (
  sessionId: string,
  queueId: string | null,
  index: number,
  contactKey: string,
): string => `${sessionId}:${queueId ?? 'none'}:${index}:${contactKey}`;

/**
 * Finalize the in-flight call in `state.dialingState` and clear `inFlight`.
 * Returns the finalized entry (so advance logic can branch on whether it
 * reached end-of-queue).
 */
const finalizeCurrent = (state: PowerDialerState): PowerDialerState => {
  const { inFlight } = state;
  if (!inFlight) {
    return state;
  }
  const finalState: DialingState = inFlight.everConnected ? 'connected' : 'no-answer';
  return {
    ...state,
    inFlight: null,
    dialingState: { ...state.dialingState, [inFlight.contactKey]: finalState },
  };
};

/**
 * Advance the cursor, or terminate the session when we'd run off the end of
 * the queue. Hitting the last contact is the natural "we're done" signal.
 */
const advanceOrEnd = (state: PowerDialerState): PowerDialerState => {
  const nextIndex = state.currentIndex + 1;
  if (nextIndex >= state.contacts.length) {
    return { ...state, phase: 'ended' };
  }
  return { ...state, currentIndex: nextIndex };
};

/**
 * User-driven skip can park one past the snapshot so a live queue append can
 * continue the session. Natural call completion uses `advanceOrEnd` instead.
 */
const advanceOrPark = (state: PowerDialerState): PowerDialerState => {
  const nextIndex = state.currentIndex + 1;
  if (nextIndex >= state.contacts.length) {
    return { ...state, currentIndex: state.contacts.length };
  }
  return { ...state, currentIndex: nextIndex };
};

export const powerDialerReducer = (
  state: PowerDialerState,
  action: PowerDialerAction,
): PowerDialerState => {
  switch (action.type) {
    case 'START_REQUESTED': {
      if (state.phase !== 'idle') {
        return state;
      }
      return { ...state, phase: 'countdown' };
    }

    case 'COUNTDOWN_CANCELLED': {
      if (state.phase !== 'countdown') {
        return state;
      }
      return { ...INITIAL_STATE };
    }

    case 'START_SESSION': {
      if (state.phase !== 'countdown') {
        return state;
      }
      if (action.contacts.length === 0) {
        return state;
      }
      return {
        ...INITIAL_STATE,
        phase: 'dialing',
        sessionId: action.sessionId,
        queueId: action.queueId,
        line: action.line,
        contacts: action.contacts,
        currentIndex: 0,
      };
    }

    case 'DIAL_CONTACT': {
      if (state.phase !== 'dialing') {
        return state;
      }
      // Idempotency: ignore re-entry for the same attempt.
      if (state.inFlight?.attemptId === action.attemptId) {
        return state;
      }
      // Don't clobber a live call.
      if (state.inFlight) {
        return state;
      }
      return {
        ...state,
        inFlight: {
          contactKey: action.contactKey,
          attemptId: action.attemptId,
          everConnected: false,
          skipRequested: false,
        },
        dialingState: { ...state.dialingState, [action.contactKey]: 'ringing' },
      };
    }

    case 'DIAL_FAILED': {
      if (state.phase !== 'dialing') {
        return state;
      }
      const next: PowerDialerState = {
        ...state,
        inFlight: null,
        dialingState: { ...state.dialingState, [action.contactKey]: 'no-answer' },
      };
      return advanceOrEnd(next);
    }

    case 'CALL_START_UNRECOVERABLE': {
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return state;
      }
      if (
        state.inFlight?.contactKey !== action.contactKey ||
        state.inFlight.attemptId !== action.attemptId
      ) {
        return state;
      }
      if (state.inFlight.skipRequested) {
        return advanceOrPark(finalizeCurrent(state));
      }
      return {
        ...state,
        phase: 'ended',
        inFlight: null,
        dialingState: { ...state.dialingState, [action.contactKey]: 'no-answer' },
      };
    }

    case 'CALL_STATUS_CHANGED': {
      const { prev, next } = action;
      if (!state.inFlight) {
        return state;
      }
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return state;
      }
      const contactKey = state.inFlight.contactKey;

      // Mid-call transitions. Phase doesn't matter — we mirror status even if paused.
      if (next === 'initiating' || next === 'ringing') {
        return {
          ...state,
          dialingState: { ...state.dialingState, [contactKey]: 'ringing' },
        };
      }
      if (next === 'in-progress') {
        return {
          ...state,
          inFlight: { ...state.inFlight, everConnected: true },
          dialingState: { ...state.dialingState, [contactKey]: 'connected' },
        };
      }

      // `initiating → idle` means the SDK never returned a call id (for
      // example unrecoverable Vonage session/auth failure). That is not a
      // callee no-answer; stop the session so we don't walk the whole queue.
      if (
        next === 'idle' &&
        prev === 'initiating' &&
        !state.inFlight.everConnected &&
        !state.inFlight.skipRequested
      ) {
        return { ...finalizeCurrent(state), phase: 'ended' };
      }

      // Terminal transition: * → idle.
      //
      // Any hangup advances. We previously gated on `inFlight.everConnected`,
      // but the SDK sometimes reports 'ringing' for calls that actually
      // connected (carrier/codec quirks) — which meant a manual hangup from
      // the call bar didn't advance. Treat every terminal idle uniformly:
      // finalize the row (connected if we ever saw in-progress, otherwise
      // no-answer) and move to the next contact. `paused` finalizes but
      // never advances until `RESUME`.
      if (next === 'idle' && prev !== 'idle') {
        const wasSkipRequested = state.inFlight.skipRequested;
        const finalized = finalizeCurrent(state);
        if (state.phase === 'paused') {
          return wasSkipRequested ? advanceOrPark(finalized) : finalized;
        }
        return wasSkipRequested ? advanceOrPark(finalized) : advanceOrEnd(finalized);
      }
      return state;
    }

    case 'SKIP_REQUESTED': {
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return state;
      }
      if (!state.inFlight || state.inFlight.skipRequested) {
        return state;
      }
      return {
        ...state,
        inFlight: { ...state.inFlight, skipRequested: true },
      };
    }

    case 'ADVANCE_REQUESTED': {
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return state;
      }
      if (state.inFlight) {
        return state;
      }
      // Finalize the current row if it wasn't already finalized by a terminal
      // callStatus transition.
      let next = state;
      const currentContact = state.contacts[state.currentIndex];
      const currentContactKey = currentContact ? getSessionContactKey(currentContact) : undefined;
      if (currentContactKey && !state.dialingState[currentContactKey]) {
        next = {
          ...state,
          dialingState: { ...state.dialingState, [currentContactKey]: 'no-answer' },
        };
      }
      // User-driven skip keeps the session alive even if we walk past the last
      // contact — new numbers can still be pushed onto the live queue. The
      // cursor parks at the end of the snapshot; Effect A's
      // `currentIndex >= contacts.length` guard prevents re-dialing, and
      // QUEUE_MUTATED will pick up any newly appended numbers at that position.
      const nextIndex = next.currentIndex + 1;
      if (nextIndex >= next.contacts.length) {
        return { ...next, currentIndex: next.contacts.length };
      }
      return { ...next, currentIndex: nextIndex };
    }

    case 'PAUSE': {
      if (state.phase !== 'dialing') {
        return state;
      }
      return { ...state, phase: 'paused' };
    }

    case 'RESUME': {
      if (state.phase !== 'paused') {
        return state;
      }
      return { ...state, phase: 'dialing' };
    }

    case 'END_REQUESTED': {
      if (state.phase === 'idle' || state.phase === 'ended') {
        return state;
      }
      return { ...state, phase: 'ended' };
    }

    case 'SESSION_CLAIMED_ELSEWHERE': {
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return state;
      }
      if (state.sessionId !== action.sessionId) {
        return state;
      }
      return { ...INITIAL_STATE };
    }

    case 'ENDED_CLEANUP_COMPLETE': {
      if (state.phase !== 'ended') {
        return state;
      }
      return { ...INITIAL_STATE };
    }

    case 'QUEUE_MUTATED': {
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return state;
      }
      if (action.contacts.length === 0) {
        if (state.inFlight) {
          return state;
        }
        return {
          ...state,
          phase: 'ended',
          queueId: action.queueId,
          contacts: [],
          currentIndex: 0,
        };
      }

      const current = state.contacts[state.currentIndex];
      const currentKey = current ? getSessionContactKey(current) : undefined;
      if (
        state.inFlight &&
        currentKey === state.inFlight.contactKey &&
        !action.contacts.some((c) => getSessionContactKey(c) === currentKey)
      ) {
        return state;
      }

      // Try to pin to the current contact's new position. This applies even
      // when the queue id is unchanged because the API may reorder or remove
      // numbers inside the same queue.
      const newIndex = currentKey
        ? action.contacts.findIndex((c) => getSessionContactKey(c) === currentKey)
        : -1;

      if (newIndex === -1 && currentKey) {
        // Current contact disappeared. Finalize row, move cursor.
        return {
          ...state,
          queueId: action.queueId,
          contacts: action.contacts,
          inFlight: null,
          currentIndex: Math.min(state.currentIndex, action.contacts.length - 1),
          dialingState: {
            ...state.dialingState,
            [currentKey]: state.dialingState[currentKey] ?? 'no-answer',
          },
        };
      }

      return {
        ...state,
        queueId: action.queueId,
        contacts: action.contacts,
        currentIndex:
          newIndex >= 0 ? newIndex : Math.min(state.currentIndex, action.contacts.length - 1),
      };
    }

    case 'RESTART_FROM_INDEX': {
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return state;
      }
      if (state.inFlight) {
        return state;
      }
      if (action.index < 0 || action.index >= state.contacts.length) {
        return state;
      }
      return {
        ...state,
        phase: 'dialing',
        currentIndex: action.index,
        inFlight: null,
      };
    }

    default:
      return state;
  }
};
